import { useState } from "react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import PageHeader from "@/components/PageHeader";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, Search, Download, Trash2, ShieldAlert, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLOR = { aktiv: "bg-[hsl(122_39%_49%/0.15)] text-[hsl(122_39%_65%)]", gesperrt: "bg-[hsl(0_66%_60%/0.15)] text-[hsl(0_66%_72%)]", inaktiv: "bg-secondary text-muted-foreground" };

const STAGE_PRETTY = { orientierung: "Orientierung", struktur: "Struktur", optimierung: "Optimierung" };
const STAGE_COLOR = {
  orientierung: "bg-[hsl(38_92%_50%/0.14)] text-[hsl(38_92%_66%)] border-[hsl(38_92%_50%/0.28)]",
  struktur:     "bg-[hsl(200_84%_50%/0.14)] text-[hsl(200_84%_70%)] border-[hsl(200_84%_50%/0.28)]",
  optimierung:  "bg-[hsl(263_70%_60%/0.14)] text-[hsl(263_70%_74%)] border-[hsl(263_70%_60%/0.28)]",
};

export default function AdminMembers() {
  const { user: me } = useAuth();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const qc = useQueryClient();

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["admin-users", search],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set("q", search);
      const r = await axios.get(`${API}/admin/users?${params}`, { withCredentials: true });
      return r.data;
    },
  });

  const patchUser = useMutation({
    mutationFn: async ({ id, patch }) => (await axios.patch(`${API}/admin/users/${id}`, patch, { withCredentials: true })).data,
    onSuccess: (data) => { setSelected(data); qc.invalidateQueries({ queryKey: ["admin-users"] }); toast.success("Nutzer aktualisiert"); },
    onError: (e) => toast.error("Update fehlgeschlagen", { description: e?.response?.data?.detail }),
  });

  const deleteUser = useMutation({
    mutationFn: async (id) => (await axios.delete(`${API}/admin/users/${id}`, { withCredentials: true })).data,
    onSuccess: (data) => {
      const total = Object.values(data.cascade_counts || {}).reduce((s, v) => s + v, 0);
      toast.success("Nutzer gelöscht", { description: `${total} zugehörige Datensätze entfernt (DSGVO Art. 17).` });
      setDeleteConfirm(null); setSelected(null); setConfirmText("");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (e) => toast.error("Löschen fehlgeschlagen", { description: e?.response?.data?.detail }),
  });

  const exportData = (u) => {
    window.location.href = `${BACKEND}/api/admin/users/${u.id}/export`;
    toast.info("Datenexport startet", { description: `Nutzerdaten von ${u.email} als JSON.` });
  };

  return (
    <div>
      <PageHeader eyebrow="Admin" title="Mitgliederverwaltung" description="Verwaltung aller Nutzerkonten — DSGVO-konform mit Datenexport (Art. 20) und Recht auf Löschung (Art. 17)." testid="admin-members-header" />

      <div className="card-surface p-4 mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
          <Input placeholder="Suchen: E-Mail oder Name …" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="admin-members-search" />
        </div>
      </div>

      <div className="card-surface p-0 overflow-hidden">
        <Table data-testid="admin-members-table">
          <TableHeader><TableRow>
            <TableHead>Nutzer</TableHead><TableHead>Nutzergruppe</TableHead><TableHead>Rolle</TableHead><TableHead>Status</TableHead>
            <TableHead className="text-right">Trades</TableHead><TableHead className="text-right">Strategien</TableHead>
            <TableHead>Angelegt</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {isLoading && (<TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Lädt…</TableCell></TableRow>)}
            {!isLoading && users.length === 0 && (<TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Keine Nutzer gefunden.</TableCell></TableRow>)}
            {users.map((u) => (
              <TableRow key={u.id} className="cursor-pointer hover:bg-secondary/40" onClick={() => setSelected(u)} data-testid={`admin-member-row-${u.id}`}>
                <TableCell>
                  <div className="text-sm">{u.name || "—"}</div>
                  <div className="text-xs text-muted-foreground">{u.email}</div>
                </TableCell>
                <TableCell>
                  {u.onboarding?.stage ? (
                    <Badge className={`rounded-full border ${STAGE_COLOR[u.onboarding.stage] || ""}`} data-testid={`admin-member-stage-${u.id}`}>
                      {STAGE_PRETTY[u.onboarding.stage] || u.onboarding.stage}
                    </Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {u.role === "admin" ? (
                    <Badge className="rounded-full bg-primary/20 text-primary"><ShieldCheck className="h-3 w-3 mr-1" strokeWidth={2} /> Admin</Badge>
                  ) : (<Badge variant="outline" className="rounded-full">Nutzer</Badge>)}
                </TableCell>
                <TableCell><Badge className={`rounded-full ${STATUS_COLOR[u.status] || ""}`}>{u.status || "—"}</Badge></TableCell>
                <TableCell className="text-right font-mono text-xs">{u.stats?.trades ?? 0}</TableCell>
                <TableCell className="text-right font-mono text-xs">{u.stats?.strategies ?? 0}</TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{new Date(u.created_at).toLocaleDateString("de-DE")}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!selected && !deleteConfirm} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="admin-member-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>{selected.name || selected.email}</DialogTitle>
                <DialogDescription>{selected.email} · angelegt {new Date(selected.created_at).toLocaleDateString("de-DE")}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {selected.onboarding?.stage && (
                  <div className="rounded-lg border border-border p-3 bg-secondary/30" data-testid="admin-member-onboarding">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Nutzergruppe (bei Registrierung)</div>
                    <div className="mt-1 flex items-center gap-2">
                      <Badge className={`rounded-full border ${STAGE_COLOR[selected.onboarding.stage] || ""}`}>
                        {STAGE_PRETTY[selected.onboarding.stage] || selected.onboarding.stage}
                      </Badge>
                      {selected.onboarding.goal && (
                        <span className="text-xs text-muted-foreground">Ziel: {selected.onboarding.goal}</span>
                      )}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-4 gap-2 text-xs">
                  {["trades","strategies","broker_connections","tickets"].map((k) => (
                    <div key={k} className="rounded-md border border-border p-2">
                      <div className="uppercase text-[10px] text-muted-foreground">{k.replace("_"," ")}</div>
                      <div className="font-mono text-lg mt-1">{selected.stats?.[k] ?? 0}</div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Rolle</div>
                    <Select value={selected.role} onValueChange={(v) => patchUser.mutate({ id: selected.id, patch: { role: v } })} disabled={selected.id === me?.id}>
                      <SelectTrigger data-testid="admin-member-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">Nutzer</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Status</div>
                    <Select value={selected.status} onValueChange={(v) => patchUser.mutate({ id: selected.id, patch: { status: v } })}>
                      <SelectTrigger data-testid="admin-member-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="aktiv">Aktiv</SelectItem>
                        <SelectItem value="gesperrt">Gesperrt</SelectItem>
                        <SelectItem value="inaktiv">Inaktiv</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="rounded-lg border border-border p-3 bg-secondary/30">
                  <div className="text-xs font-medium mb-2 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-primary" strokeWidth={1.5} /> DSGVO-Rechte</div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => exportData(selected)} className="rounded-full flex-1" data-testid="admin-member-export">
                      <Download className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Daten exportieren (Art. 20)
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(selected)} disabled={selected.id === me?.id} className="rounded-full flex-1 text-[hsl(0_66%_72%)] border-[hsl(0_66%_60%/0.3)] hover:bg-[hsl(0_66%_60%/0.1)]" data-testid="admin-member-delete-open">
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Konto löschen (Art. 17)
                    </Button>
                  </div>
                  {selected.id === me?.id && (
                    <div className="text-[11px] text-muted-foreground mt-2">Du kannst dein eigenes Konto nicht löschen oder herabstufen.</div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteConfirm} onOpenChange={(v) => { if (!v) { setDeleteConfirm(null); setConfirmText(""); } }}>
        <DialogContent className="max-w-md" data-testid="admin-member-delete-dialog">
          {deleteConfirm && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-[hsl(0_66%_72%)]">
                  <ShieldAlert className="h-5 w-5" strokeWidth={1.5} /> Konto endgültig löschen
                </DialogTitle>
                <DialogDescription>
                  Diese Aktion ist <b>unwiderruflich</b> und entfernt alle Daten des Nutzers gemäß DSGVO Art. 17:
                  Trades, Strategien, Broker-Verbindungen, Steuerprofil, Support-Tickets, Skill-Scores.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="rounded-lg border border-[hsl(0_66%_60%/0.3)] bg-[hsl(0_66%_60%/0.06)] p-3 text-sm">
                  <div className="font-medium">{deleteConfirm.name || "—"}</div>
                  <div className="text-muted-foreground">{deleteConfirm.email}</div>
                  <div className="text-xs text-muted-foreground mt-2">
                    {deleteConfirm.stats?.trades ?? 0} Trades · {deleteConfirm.stats?.strategies ?? 0} Strategien · {deleteConfirm.stats?.broker_connections ?? 0} Broker-Verbindungen · {deleteConfirm.stats?.tickets ?? 0} Tickets
                  </div>
                </div>
                <div>
                  <div className="text-xs mb-1.5">Tippe zur Bestätigung <span className="font-mono font-medium">{deleteConfirm.email}</span></div>
                  <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={deleteConfirm.email} data-testid="admin-member-delete-confirm" />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" onClick={() => { setDeleteConfirm(null); setConfirmText(""); }} data-testid="admin-member-delete-cancel">Abbrechen</Button>
                  <Button
                    onClick={() => deleteUser.mutate(deleteConfirm.id)}
                    disabled={confirmText !== deleteConfirm.email || deleteUser.isPending}
                    className="rounded-full bg-[hsl(0_66%_50%)] hover:bg-[hsl(0_66%_45%)] text-white"
                    data-testid="admin-member-delete-confirm-btn"
                  >
                    {deleteUser.isPending ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Lösche…</>) : "Endgültig löschen"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
